import React, { useEffect, useRef, useState } from "react";
import { Card, Container, Form, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import api from "../../../../utils/ApiMethod";
import { AppDispatch, RootState } from "../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { logoutUser } from "../../../../redux/actions";
import { ADMIN_LOGIN_BASE_URL } from "../../../../apiservices/apiService";
import "./student-profile-modal.scss";

interface InputField {
  value: string;
  isValid: boolean;
}

interface Game {
  game?: string;
  rank?: string;
}

interface Document {
  fileName: string;
  fileUrl: string;
}

interface AadharDocument {
  fileName: string;
  fileUrlOfAadharFront: string;
  fileUrlOfAadharBack: string;
}

interface StudentFormData {
  studentId: string;
  selectedInTeam: string[];
  studentName: InputField;
  gender: InputField;
  aadharNumber: InputField;
  fatherName: InputField;
  motherName: InputField;
  dob: InputField;
  caldob: InputField;
  ageCategory: string;
  mobileNumber: InputField;
  email: InputField;
  gameName: Game[];
  height: InputField;
  weight: InputField;
  thirtyMFlingStarts: InputField;
  standingBroadJump: InputField;
  shuttleRun10MX6: InputField;
  verticalJump: InputField;
  footballBallThrow5No: InputField;
  eightHundredMetersRun: InputField;
  updatable?: boolean;
  _id?: string;
  document?: Document[];
  photo?: Document[];
  liveAadharCapturedImageData?: AadharDocument[];
}

const initialForm: StudentFormData = {
  studentId: "",
  selectedInTeam: [],
  studentName: { value: "", isValid: true },
  gender: { value: "Female", isValid: true },
  aadharNumber: { value: "", isValid: true },
  fatherName: { value: "", isValid: true },
  motherName: { value: "", isValid: true },
  dob: { value: "", isValid: true },
  caldob: { value: "", isValid: true },
  ageCategory: "U-14 (Boys)",
  mobileNumber: { value: "", isValid: true },
  email: { value: "", isValid: true },
  gameName: [],
  height: { value: "", isValid: true },
  weight: { value: "", isValid: true },
  thirtyMFlingStarts: { value: "", isValid: true },
  standingBroadJump: { value: "", isValid: true },
  shuttleRun10MX6: { value: "", isValid: true },
  verticalJump: { value: "", isValid: true },
  footballBallThrow5No: { value: "", isValid: true },
  eightHundredMetersRun: { value: "", isValid: true },
  document: [],
  photo: [],
  liveAadharCapturedImageData: [],
};

const gameOptions: Record<string, string[]> = {
  // Volleyball removed per dev-plan [29 Sept 2026] Update 2 - not an eligible sport for U-14 boys.
  "U-14-boys": ["Football", "Kabaddi", "Cycling (3km)", "Long Jump (Athletics)", "60m Sprint (Athletics)", "600m Long Run (Athletics)"],
  "U-14-girls": ["Kabaddi", "Cycling (3km)", "Long Jump (Athletics)", "60m Sprint (Athletics)", "600m Long Run (Athletics)"],
  "U-16-boys": ["Football", "Kabaddi", "Volleyball", "Cycling (5km)", "Long Jump (Athletics)", "100m Sprint (Athletics)", "800m Long Run (Athletics)"],
  "U-16-girls": ["Kabaddi", "Cycling (5km)", "Long Jump (Athletics)", "100m Sprint (Athletics)", "800m Long Run (Athletics)"],
};

const customStyles = {
  menu: (provided: any) => ({ ...provided, maxHeight: 200, overflowY: "auto" }),
  control: (provided: any) => ({
    ...provided,
    backgroundColor: "#f4f5f8",
    border: "1px solid transparent",
    minHeight: 40,
  }),
};

interface ProfileStudentRegistrationFormProps {
  studentId?: string;
  readOnly?: boolean;
}

const ProfileStudentRegistrationForm: React.FC<ProfileStudentRegistrationFormProps> = ({
  studentId: modalStudentId,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState<StudentFormData>(initialForm);
  const [isError, setIsError] = useState(false);
  const [isResponse, setIsResponse] = useState("");
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [finalSubmitLoading, setFinalSubmitLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [aadharUploading, setAadharUploading] = useState(false);
  const latestRequestRef = useRef<string>("");

  const { studentId: paramStudentId } = useParams<{ studentId?: string }>();
  const studentId = modalStudentId ?? paramStudentId ?? "";
  const dobRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const aadharFrontInputRef = useRef<HTMLInputElement>(null);
  const aadharBackInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => ({ user: state.Auth.user }));
  const profileId = user?._id ?? user?.user?._id ?? "";
  const Key_Key = user?.moniterd_ttl ?? user?.user?.moniterd_ttl ?? "";

  const isModal = !!modalStudentId;
  const isEditable = !!formData?.updatable && !readOnly;

  const formatDateString = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchStudentProfiles = async () => {
    const requestId = studentId;
    try {
      const response = await api.fetchDataAuth(
        `/api/v5/student-profile/cluster/${studentId}/${profileId}/${Key_Key}`
      );
      if (latestRequestRef.current !== requestId) return;
      const fetchedData = response?.data?.data;
      if (!fetchedData) return;

      setFormData({
        studentId: fetchedData.studentId,
        selectedInTeam: fetchedData.selectedInTeam || [],
        studentName: { value: fetchedData.studentName || "", isValid: true },
        gender: { value: fetchedData.gender || "", isValid: true },
        aadharNumber: { value: fetchedData.aadharNumber || "", isValid: true },
        fatherName: { value: fetchedData.fatherName || "", isValid: true },
        motherName: { value: fetchedData.motherName || "", isValid: true },
        updatable: fetchedData.updatable,
        _id: fetchedData._id,
        dob: { value: fetchedData.dob ? formatDateString(fetchedData.dob) : "", isValid: true },
        caldob: { value: fetchedData.caldob || "", isValid: true },
        ageCategory: fetchedData.ageCategory || "U-14 (Boys)",
        mobileNumber: { value: fetchedData.mobileNumber || "", isValid: true },
        email: { value: fetchedData.email || "", isValid: true },
        gameName: fetchedData.gameName || [],
        document: fetchedData.document || [],
        photo: fetchedData.photo || [],
        height: { value: fetchedData.height || "", isValid: true },
        weight: { value: fetchedData.weight || "", isValid: true },
        thirtyMFlingStarts: { value: fetchedData.thirtyMFlingStarts || "", isValid: true },
        standingBroadJump: { value: fetchedData.standingBroadJump || "", isValid: true },
        shuttleRun10MX6: { value: fetchedData.shuttleRun10MX6 || "", isValid: true },
        verticalJump: { value: fetchedData.verticalJump || "", isValid: true },
        footballBallThrow5No: { value: fetchedData.footballBallThrow5No || "", isValid: true },
        eightHundredMetersRun: { value: fetchedData.eightHundredMetersRun || "", isValid: true },
        liveAadharCapturedImageData: fetchedData?.liveAadharCapturedImageData || [],
      });
    } catch (error) {
      console.error("Error fetching student profile:", error);
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsProfileLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!studentId) return;
    latestRequestRef.current = studentId;
    setIsProfileLoading(true);
    fetchStudentProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    let validRegex: RegExp;
    switch (name) {
      case "height":
        validRegex = /^\d{0,3}$/;
        break;
      case "eightHundredMetersRun":
        validRegex = /^([0-5]?[0-9]):([0-5]?[0-9]):([0-9]{1,3})$/;
        break;
      default:
        validRegex = /^\d{0,3}(\.\d{0,2})?$/;
    }
    let formattedValue = value;
    if (name === "eightHundredMetersRun") {
      const cleaned = value.replace(/\D+/g, "");
      const match = cleaned.match(/^(\d{0,2})(\d{0,2})(\d{0,3})$/);
      formattedValue = match ? [match[1], match[2], match[3]].filter(Boolean).join(":") : value;
    }
    const isValidValue = validRegex.test(formattedValue);
    if (["height", "weight", "thirtyMFlingStarts", "standingBroadJump", "shuttleRun10MX6", "verticalJump", "footballBallThrow5No", "eightHundredMetersRun"].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: { value: formattedValue, isValid: isValidValue } }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: { value, isValid: true } }));
    }
  };

  const getInputStyle = (isValid: boolean) => (isValid ? undefined : { borderColor: "#e0384c" });

  const handleGameChange = (selectedOptions: any) => {
    const selectedGames = selectedOptions ? selectedOptions.map((option: any) => ({ game: option.value })) : [];
    setFormData((prev) => ({ ...prev, gameName: selectedGames }));
  };

  const ageGroupKey = formData.ageCategory.startsWith("U-14 (Boys)")
    ? "U-14-boys"
    : formData.ageCategory.startsWith("U-14 (Girls)")
    ? "U-14-girls"
    : formData.ageCategory.startsWith("U-16 (Boys)")
    ? "U-16-boys"
    : "U-16-girls";
  const availableGames = (gameOptions[ageGroupKey] || []).map((game) => ({ value: game, label: game }));

  const updateStudentProfile = async (action: "save" | "submit") => {
    setIsError(false);
    if (action === "submit") {
      const missingBatteryField = [
        formData.height.value,
        formData.weight.value,
        formData.thirtyMFlingStarts.value,
        formData.standingBroadJump.value,
        formData.shuttleRun10MX6.value,
        formData.verticalJump.value,
      ].some((v) => !v);
      if (missingBatteryField) {
        setIsError(true);
        setIsResponse("Please enter every battery test field before final submission.");
        return;
      }
      const isConfirmed = window.confirm(
        "Please review all details carefully before proceeding. Once you submit, you will not be able to make further edits."
      );
      if (!isConfirmed) return;
    }

    const setLoading = action === "save" ? setSaveButtonLoading : setFinalSubmitLoading;
    setLoading(true);
    try {
      const payload = {
        studentName: formData.studentName.value,
        gender: formData.gender.value,
        aadharNumber: formData.aadharNumber.value,
        fatherName: formData.fatherName.value,
        motherName: formData.motherName.value,
        dob: formData.dob.value,
        caldob: formData.caldob.value,
        ageCategory: formData.ageCategory,
        mobileNumber: formData.mobileNumber.value,
        email: formData.email.value,
        gameName: formData.gameName,
        height: formData.height.value,
        weight: formData.weight.value,
        thirtyMFlingStarts: formData.thirtyMFlingStarts.value,
        standingBroadJump: formData.standingBroadJump.value,
        shuttleRun10MX6: formData.shuttleRun10MX6.value,
        verticalJump: formData.verticalJump.value,
        footballBallThrow5No: formData.footballBallThrow5No.value,
        eightHundredMetersRun: formData.eightHundredMetersRun.value,
      };

      const response = await api.updatePatchAnyData(
        `/api/v5/cluster/student/update/${profileId}/${studentId}/${action}/${Key_Key}`,
        payload
      );

      if (response?.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL;
        return;
      }
      if (response?.status === 200 || response?.status === 201) {
        if (action === "submit") {
          alert("Your record has been submitted successfully.");
        }
        fetchStudentProfiles();
        return;
      }
      setIsError(true);
      setIsResponse(response?.message || response?.data?.message || "Could not update the profile.");
    } catch (error: any) {
      setIsError(true);
      setIsResponse(error?.message || "Could not update the profile.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !formData._id) return;
    setImageUploading(true);
    try {
      const body = new FormData();
      body.append("photo", file);
      const response = await api.updatePatchData(
        `/api/v5/cluster/student/update-image/${profileId}/${formData._id}/${Key_Key}`,
        body
      );
      if (response?.status === 200) {
        fetchStudentProfiles();
      } else {
        alert(response?.message || "Could not update the photo.");
      }
    } finally {
      setImageUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleAadharFilesChange = async () => {
    const front = aadharFrontInputRef.current?.files?.[0];
    const back = aadharBackInputRef.current?.files?.[0];
    if (!front || !back || !formData._id) return;
    setAadharUploading(true);
    try {
      const body = new FormData();
      body.append("aadharFrontImage", front);
      body.append("aadharBackImage", back);
      const response = await api.updatePatchData(
        `/api/v5/cluster/student/update-aadhar-images/${profileId}/${formData._id}/${Key_Key}`,
        body
      );
      if (response?.status === 200) {
        fetchStudentProfiles();
      } else {
        alert(response?.message || "Could not update the Aadhaar images.");
      }
    } finally {
      setAadharUploading(false);
      if (aadharFrontInputRef.current) aadharFrontInputRef.current.value = "";
      if (aadharBackInputRef.current) aadharBackInputRef.current.value = "";
    }
  };

  if (isModal && isProfileLoading) {
    return (
      <div className="pf-form">
        <div className="pf-layout">
          <div className="pf-sidebar">
            <div className="pf-side-card">
              <div className="pf-skel-line pf-skel-w-50" />
              <div className="pf-skel-photo mt-2" />
            </div>
          </div>
          <div className="pf-main">
            <div className="pf-section">
              <div className="pf-skel-line pf-skel-w-30 pf-skel-title" />
              <div className="pf-grid">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div className="pf-field" key={i}>
                    <div className="pf-skel-line pf-skel-w-50 pf-skel-label" />
                    <div className="pf-skel-input" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formContent = (
    <Form className="pf-form">
      {!isModal && <h2 className="pf-page-title">Student Profile Details</h2>}
      <div className="pf-layout">
        <div className="pf-sidebar">
          <div className="pf-side-card">
            <h4>Student Photo</h4>
            <div className="pf-photo-card">
              {Array.isArray(formData.photo) && formData.photo.length > 0 ? (
                <img
                  src={formData.photo[formData.photo.length - 1]?.fileUrl ?? "#"}
                  alt={formData.photo[formData.photo.length - 1]?.fileName ?? "Student"}
                />
              ) : (
                <div className="pf-photo-placeholder">
                  <i className="uil uil-user-circle" />
                  <span>No image available.</span>
                </div>
              )}
            </div>
            {isEditable && (
              <div className="mt-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="form-control form-control-sm"
                  onChange={handlePhotoFileChange}
                  disabled={imageUploading}
                />
                {imageUploading && <small className="text-muted">Uploading...</small>}
              </div>
            )}
          </div>

          <div className="pf-side-card">
            <h4>Aadhaar Document</h4>
            {Array.isArray(formData.liveAadharCapturedImageData) && formData.liveAadharCapturedImageData.length > 0 ? (
              <>
                <a
                  href={formData.liveAadharCapturedImageData[formData.liveAadharCapturedImageData.length - 1]?.fileUrlOfAadharFront ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pf-doc-link"
                >
                  Front Image Of Aadhar
                </a>
                <a
                  href={formData.liveAadharCapturedImageData[formData.liveAadharCapturedImageData.length - 1]?.fileUrlOfAadharBack ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pf-doc-link"
                >
                  Back Image Of Aadhar
                </a>
              </>
            ) : Array.isArray(formData.document) && formData.document.length > 0 ? (
              <a
                href={formData.document[formData.document.length - 1]?.fileUrl ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="pf-doc-link"
              >
                {formData.document[formData.document.length - 1]?.fileName ?? "Aadhaar document"}
              </a>
            ) : (
              <p className="pf-doc-missing">No documents available.</p>
            )}
            {isEditable && (
              <div className="mt-2 d-flex flex-column gap-1">
                <input ref={aadharFrontInputRef} type="file" accept="image/*" className="form-control form-control-sm" placeholder="Front" />
                <input ref={aadharBackInputRef} type="file" accept="image/*" className="form-control form-control-sm" placeholder="Back" />
                <Button size="sm" variant="outline-primary" onClick={handleAadharFilesChange} disabled={aadharUploading}>
                  {aadharUploading ? "Uploading..." : "Update Aadhaar Images"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="pf-main">
          <div className="pf-section">
            <h3 className="pf-section-title">Personal Information</h3>
            <div className="pf-grid">
              <div className="pf-field">
                <Form.Label>
                  Student's Full Name <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="studentName"
                  value={formData.studentName.value}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>
                  Gender <span className="pf-required">*</span>
                </Form.Label>
                <div className="pf-gender-toggle">
                  <Form.Check
                    inline
                    label="Girl"
                    name="gender"
                    type="radio"
                    id="genderFemale"
                    value="Female"
                    checked={formData.gender.value === "Female"}
                    onChange={handleChange}
                    disabled={!isEditable}
                  />
                  <Form.Check
                    inline
                    label="Boy"
                    name="gender"
                    type="radio"
                    id="genderMale"
                    value="Male"
                    checked={formData.gender.value === "Male"}
                    onChange={handleChange}
                    disabled={!isEditable}
                  />
                </div>
              </div>
              <div className="pf-field">
                <Form.Label>
                  Aadhar Number <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="aadharNumber"
                  value={formData.aadharNumber.value}
                  onChange={handleChange}
                  maxLength={12}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>
                  Mother's Full Name <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="motherName"
                  value={formData.motherName.value}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>
                  Father's Full Name <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="fatherName"
                  value={formData.fatherName.value}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>
                  Date of Birth <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={formData.dob.value}
                  onChange={handleChange}
                  ref={dobRef}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>Age as on 31st Dec</Form.Label>
                <Form.Control type="text" name="caldob" value={formData.caldob.value} readOnly />
              </div>
              <div className="pf-field">
                <Form.Label>Category</Form.Label>
                <Form.Control type="text" name="ageCategory" value={formData.ageCategory} readOnly />
              </div>
              <div className="pf-field">
                <Form.Label>
                  Parent/Guardian Mobile Number <span className="pf-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="mobileNumber"
                  value={formData.mobileNumber.value}
                  onChange={handleChange}
                  maxLength={10}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field">
                <Form.Label>E-Mail Id</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email.value}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
              </div>
              <div className="pf-field pf-field--full">
                <Form.Label>
                  Select Your Game <span className="pf-required">*</span>
                </Form.Label>
                <Select
                  isMulti
                  name="gameName"
                  options={availableGames}
                  value={availableGames.filter((option) =>
                    formData.gameName.some((selected) => selected.game === option.value)
                  )}
                  onChange={handleGameChange}
                  closeMenuOnSelect={false}
                  styles={customStyles}
                  isDisabled={!isEditable}
                />
              </div>
            </div>
          </div>

          <div className="pf-section">
            <h3 className="pf-section-title">Battery Test Details</h3>
            <div className="pf-grid">
              <div className="pf-field">
                <Form.Label>Height (in cm)</Form.Label>
                <Form.Control
                  type="text"
                  name="height"
                  value={formData.height.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.height.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.height.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>Weight (in Kg)</Form.Label>
                <Form.Control
                  type="text"
                  name="weight"
                  value={formData.weight.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.weight.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.weight.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>30 Mtr sprint (in sec.)</Form.Label>
                <Form.Control
                  type="text"
                  name="thirtyMFlingStarts"
                  value={formData.thirtyMFlingStarts.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.thirtyMFlingStarts.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.thirtyMFlingStarts.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>6X10 Mtr shuttle run (in second)</Form.Label>
                <Form.Control
                  type="text"
                  name="shuttleRun10MX6"
                  value={formData.shuttleRun10MX6.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.shuttleRun10MX6.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.shuttleRun10MX6.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>Vertical Jump (in cm)</Form.Label>
                <Form.Control
                  type="text"
                  name="verticalJump"
                  value={formData.verticalJump.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.verticalJump.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.verticalJump.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>Standing Broad jump (in m)</Form.Label>
                <Form.Control
                  type="text"
                  name="standingBroadJump"
                  value={formData.standingBroadJump.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.standingBroadJump.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.standingBroadJump.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>Football Throw (in m)</Form.Label>
                <Form.Control
                  type="text"
                  name="footballBallThrow5No"
                  value={formData.footballBallThrow5No.value}
                  onChange={handleChange}
                  style={getInputStyle(formData.footballBallThrow5No.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.footballBallThrow5No.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
              <div className="pf-field">
                <Form.Label>800 mts Run (in mm:ss:sss)</Form.Label>
                <Form.Control
                  type="text"
                  name="eightHundredMetersRun"
                  value={formData.eightHundredMetersRun.value}
                  onChange={handleChange}
                  placeholder="MM:SS:SSS"
                  style={getInputStyle(formData.eightHundredMetersRun.isValid)}
                  readOnly={!isEditable}
                />
                {!formData.eightHundredMetersRun.isValid && <p className="pf-error">Invalid input.</p>}
              </div>
            </div>

            {isError && (
              <Alert variant="danger" className="my-2">
                {isResponse}
              </Alert>
            )}

            {isEditable && (
              <div className="pf-actions">
                <Button variant="secondary" onClick={() => updateStudentProfile("save")} disabled={saveButtonLoading}>
                  {saveButtonLoading ? "Saving..." : "Save"}
                </Button>
                <Button variant="primary" onClick={() => updateStudentProfile("submit")} disabled={finalSubmitLoading}>
                  {finalSubmitLoading ? "Submitting..." : "Final Submission"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Form>
  );

  if (isModal) {
    return formContent;
  }

  return (
    <Container>
      <Card className="my-4">
        <Card.Body>{formContent}</Card.Body>
      </Card>
    </Container>
  );
};

export default ProfileStudentRegistrationForm;
